import queryString from "query-string";
import {
  company,
  individual,
  otherEntity,
  partnership,
  plc,
  soleTrader,
  trust,
} from "../../modules/profiles/profile.constant";
import type { KeyPartiesType } from "../../modules/profiles/types/profile.type";
import type { AliasType } from "../../modules/profiles/types/profileDetails.type";
import type { User } from "../../shared/types/user.type";
import type { DateVal, NullableStringDateVal } from "../types";

export const createQueryString = (queryObject: object): string => {
  return queryString.stringify(queryObject);
};

export const showHyphenIfNoValue = (
  value?: string | DateVal | null
): string | DateVal | null => {
  if (typeof value === "string") {
    if (value.trim() === "") {
      return "-";
    }
    return value;
  }
  return value ?? "-";
};

export const isAllValueForKeyEmptyString = <
  TObject extends object,
  K extends keyof TObject
>(
  arrayOfObjects: TObject[],
  key: K
): boolean => {
  return arrayOfObjects.every((obj) => obj[key] === "");
};

export const getItemOnIndex = (
  message: string,
  key: string,
  index: number
): string | undefined | void => {
  const translatedText = message.split(key);
  if (translatedText.length > index) return translatedText[index];
};

export const getFilteredAliases = (aliases: AliasType[]): AliasType[] =>
  aliases.filter((obj) => obj["alias"] !== "");

export interface SanitisableObj {
  [K: string]: string | string[] | null | SanitisableObj;
}

const isStringArray = (value: unknown): value is string[] => {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
};

const checkEmptyValue = (
  value: string,
  sanitised: SanitisableObj,
  key: string
): void => {
  if (value === "" || value.trim() === "") {
    sanitised[key] = null;
  } else {
    sanitised[key] = value.trim();
  }
};

export const sanitiseWhiteSpace = (
  inputObj: SanitisableObj
): string | SanitisableObj | string[] | null => {
  const sanitisedObj = { ...inputObj };
  const processingArr = [[sanitisedObj, inputObj]];

  while (processingArr.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const [sanitised, original] = processingArr.pop()!;

    for (const key in original) {
      const value = original[key];

      if (value === null) {
        sanitised[key] = value;
      } else if (typeof value === "string") {
        checkEmptyValue(value, sanitised, key);
      } else if (isStringArray(value)) {
        sanitised[key] = value
          .filter((item: string) => item.trim() !== "")
          .map((item) => item.trim());
      } else if (value instanceof Date) {
        sanitised[key] = value;
      } else if (typeof value === "object") {
        const sanitisedNested = {};
        sanitised[key] = sanitisedNested;
        processingArr.push([sanitisedNested, value]);
      } else {
        sanitised[key] = value;
      }
    }
  }
  return sanitisedObj;
};

export const capitaliseFirstLetter = (
  value: string | null | undefined
): string => {
  if (!value) return "-";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const hasDuplicates = (emails: { email: string }[]): boolean => {
  const uniqueEmails = new Set();
  for (const obj of emails) {
    if (uniqueEmails.has(obj.email)) {
      return true; // Found a duplicate email
    }
    uniqueEmails.add(obj.email);
  }
  return false; // No duplicate emails found
};

export const returnSafeNumberOrCompanyId = (
  safeNumber: string | null,
  companyId: string | null
): { label: string; value: string | null } => {
  const labelAndValue = { label: "safeNumber", value: safeNumber };
  if (safeNumber) {
    return labelAndValue;
  } else if (companyId) {
    return { label: "companyId", value: companyId };
  }
  return labelAndValue;
};

export const downloadPreSignedURL = async (url: string): Promise<void> => {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getContactName = (user: User): string => {
  if (user.firstName || user.surname) {
    return (
      (user.firstName ?? "") +
      (user.firstName && user.surname ? " " : "") +
      (user.surname ?? "")
    );
  }
  return user.username ?? "";
};

export const containsText = (searchText: string, text = ""): boolean =>
  text.toLowerCase().startsWith(searchText.toLowerCase());

export const stringCheck = (value: string | null | undefined): string => {
  return value ? `${value}` : "";
};

export const nameBuilder = (keyParty: KeyPartiesType): string => {
  return `${stringCheck(keyParty.firstName)} ${stringCheck(
    keyParty.middleName
  )} ${stringCheck(keyParty.lastName)}`;
};

export const putNameBasedOnNames = (
  name: string,
  nameObject: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    name?: string;
  } = {}
): {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  name?: string;
} => {
  const nameParts = name.split(" ");
  if (name.split(" ").length > 3) {
    nameObject.firstName = nameParts[0];
    nameObject.middleName = nameParts.slice(1, -1).join(" ");
    nameObject.lastName = nameParts.at(-1);
  } else if (nameParts.length === 3) {
    nameObject.firstName = nameParts[0];
    nameObject.middleName = nameParts[1];
    nameObject.lastName = nameParts[2];
  } else if (nameParts.length === 2) {
    nameObject.firstName = nameParts[0];
    nameObject.lastName = nameParts[1];
  } else {
    nameObject.name = nameParts[0];
  }
  return nameObject;
};

export const getDocumentTypes = (profileType: string): string[] => {
  switch (profileType) {
    case "trust":
      return trust;
    case "individual":
      return individual;
    case "soleTrader":
      return soleTrader;
    case "company":
      return company;
    case "plc":
      return plc;
    case "partnership":
      return partnership;
    case "otherEntity":
      return otherEntity;
    default:
      return [];
  }
};

interface NameFields {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  name: string;
}

export const getFullName = <T extends NameFields>(values: T): string | null => {
  return values.name === ""
    ? [values.firstName, values.middleName, values.lastName]
        .filter((namePart) => namePart && namePart.trim() !== "")
        .join(" ")
    : values.name;
};

export const getIsDobRequired = (
  dateOfBirth: NullableStringDateVal
): boolean => {
  return !!dateOfBirth;
};

export const convertToBoolean = (
  value: boolean | string | undefined
): boolean => {
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return !!value;
};

export const filterBySearchTerm = <T>(
  items: T[],
  searchTerm: string,
  searchFields: Array<(_item: T) => string | number | null | undefined>
): T[] => {
  if (!searchTerm.trim()) {
    return items;
  }

  const searchLower = searchTerm.toLowerCase().trim();

  return items.filter((item) =>
    searchFields.some((getField) => {
      const fieldValue = getField(item);
      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }
      return fieldValue.toString().toLowerCase().includes(searchLower);
    })
  );
};
